import React, { useCallback, useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Trash2, Copy, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ProBadge from "@/components/ProBadge";

/**
 * Admin-only Pro Access Codes dashboard.
 *
 * Rendered inside AccountPanel when the caller is an admin. All actions
 * are server-authorised (role checked in ``comp_pro._require_admin``);
 * removing this component from the frontend does not weaken security.
 */
function _fmt(iso) {
  if (!iso) return "no expiry";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function AdminProCodes() {
  const [codes, setCodes] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState(null);   // { code } shown once
  const [form, setForm] = useState({ description: "", duration_days: 30, max_uses: 10 });
  const [grantForm, setGrantForm] = useState({ user_query: "", duration_days: 30, reason: "", send_email: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, g] = await Promise.all([
        endpoints.adminListCodes(),
        endpoints.adminListGrants(true),
      ]);
      setCodes(c.data.codes || []);
      setGrants(g.data.grants || []);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCode = async (e) => {
    e?.preventDefault?.();
    if (!form.description.trim()) return;
    setBusy(true);
    try {
      const { data } = await endpoints.adminCreateCode(form);
      setIssued(data);
      setForm({ description: "", duration_days: 30, max_uses: 10 });
      setCreating(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create code.");
    } finally {
      setBusy(false);
    }
  };

  const disableCode = async (id) => {
    if (!window.confirm("Disable this code? Existing grants keep working until they expire.")) return;
    setBusy(true);
    try {
      await endpoints.adminDisableCode(id);
      toast.success("Code disabled.");
      await load();
    } catch { toast.error("Could not disable code."); }
    finally { setBusy(false); }
  };

  const revokeGrant = async (id) => {
    if (!window.confirm("Revoke this complimentary grant? The user's data stays intact.")) return;
    setBusy(true);
    try {
      await endpoints.adminRevokeGrant(id);
      toast.success("Grant revoked.");
      await load();
    } catch { toast.error("Could not revoke grant."); }
    finally { setBusy(false); }
  };

  const directGrant = async (e) => {
    e?.preventDefault?.();
    if (!grantForm.user_query.trim()) return;
    setBusy(true);
    try {
      const { data } = await endpoints.adminDirectGrant(grantForm);
      toast.success(
        data?.email_sent
          ? "Complimentary Pro granted — email sent."
          : "Complimentary Pro granted — they'll see it next time they open N8One.",
      );
      setGrantForm({ user_query: "", duration_days: 30, reason: "", send_email: false });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not grant.");
    } finally { setBusy(false); }
  };

  return (
    <section
      data-testid="admin-pro-codes"
      className="p-6 md:p-7 border hairline rounded-md bg-[hsl(var(--paper))]"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
        <Sparkles size={12} />
        <span>Pro Access Codes</span>
        <ProBadge />
      </div>
      <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
        Issue complimentary Pro to selected users without touching Stripe. Each code is stored securely — the plaintext value is shown ONCE at creation. Save it somewhere safe.
      </p>

      {/* One-time reveal of a newly created code */}
      {issued && (
        <div
          data-testid="admin-code-issued"
          className="mt-4 p-4 rounded-md border border-dashed border-[hsl(var(--terracotta))]/60 bg-[hsl(var(--sand))]/40"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--terracotta))] flex items-center justify-between">
            <span>Save this code — it won&rsquo;t be shown again</span>
            <button type="button" onClick={() => setIssued(null)} aria-label="Dismiss" className="p-1 rounded hover:bg-[hsl(var(--sand))]"><X size={12} /></button>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <code
              data-testid="admin-code-value"
              className="font-mono text-lg tracking-widest px-3 py-1.5 rounded bg-[hsl(var(--paper))] border hairline select-all"
            >
              {issued.code}
            </code>
            <button
              type="button"
              onClick={() => { navigator.clipboard?.writeText(issued.code); toast.success("Copied."); }}
              className="p-2 rounded hover:bg-[hsl(var(--sand))] tab-link"
              aria-label="Copy"
            >
              <Copy size={14} />
            </button>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">{issued.description} · {issued.duration_days}d · {issued.max_uses} uses</span>
          </div>
        </div>
      )}

      {/* Create-code form */}
      {creating ? (
        <form onSubmit={createCode} className="mt-4 grid gap-3 sm:grid-cols-4">
          <Input
            data-testid="admin-code-description"
            placeholder="Description (e.g. Beta Testers)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="sm:col-span-2"
          />
          <Input
            data-testid="admin-code-duration"
            type="number" min="1" max="3650"
            placeholder="Days"
            value={form.duration_days}
            onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value || "0", 10) })}
          />
          <Input
            data-testid="admin-code-maxuses"
            type="number" min="1" max="100000"
            placeholder="Max uses"
            value={form.max_uses}
            onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value || "0", 10) })}
          />
          <div className="sm:col-span-4 flex items-center gap-2">
            <Button type="submit" disabled={busy} data-testid="admin-code-create-submit">
              {busy ? "Creating…" : "Create code"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
          </div>
        </form>
      ) : (
        <div className="mt-4">
          <Button onClick={() => setCreating(true)} data-testid="admin-code-new-btn">+ New Pro code</Button>
        </div>
      )}

      {/* Codes list */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] mb-2">Existing codes</div>
        {loading && <div className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</div>}
        {!loading && codes.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No codes yet.</div>}
        <ul className="space-y-2" data-testid="admin-codes-list">
          {codes.map((c) => (
            <li key={c.id} className="p-3 border hairline rounded flex items-center gap-3 flex-wrap" data-testid={`admin-code-${c.id}`}>
              <code className="font-mono text-sm">{c.prefix}·…</code>
              <span className="text-sm">{c.description}</span>
              <span className="text-xs text-[hsl(var(--muted-foreground))] tabular-nums">{c.uses}/{c.max_uses} used</span>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">exp: {_fmt(c.expires_at)}</span>
              <span className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full ${c.status === "active" ? "bg-[hsl(var(--moss))]/15 text-[hsl(var(--moss))]" : "bg-[hsl(var(--muted-foreground))]/15 text-[hsl(var(--muted-foreground))]"}`}>{c.status}</span>
              {c.status === "active" && (
                <button
                  type="button"
                  onClick={() => disableCode(c.id)}
                  disabled={busy}
                  className="ml-auto p-1 rounded hover:bg-[hsl(var(--sand))] tab-link"
                  aria-label="Disable"
                  data-testid={`admin-code-disable-${c.id}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Direct grant */}
      <div className="mt-8 pt-6 border-t hairline">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] mb-2">Grant directly to a user</div>
        <form onSubmit={directGrant} className="grid gap-3 sm:grid-cols-4">
          <Input
            data-testid="admin-grant-user"
            placeholder="email or @username"
            value={grantForm.user_query}
            onChange={(e) => setGrantForm({ ...grantForm, user_query: e.target.value })}
            className="sm:col-span-2"
          />
          <Input
            data-testid="admin-grant-duration"
            type="number" min="1" max="3650"
            placeholder="Days"
            value={grantForm.duration_days}
            onChange={(e) => setGrantForm({ ...grantForm, duration_days: parseInt(e.target.value || "0", 10) })}
          />
          <Input
            data-testid="admin-grant-reason"
            placeholder="Reason (optional)"
            value={grantForm.reason}
            onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })}
          />
          <div className="sm:col-span-4 flex items-center gap-4 flex-wrap">
            <Button type="submit" disabled={busy} data-testid="admin-grant-submit">Grant Complimentary Pro</Button>
            <label className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] cursor-pointer" data-testid="admin-grant-send-email">
              <input
                type="checkbox"
                checked={grantForm.send_email}
                onChange={(e) => setGrantForm({ ...grantForm, send_email: e.target.checked })}
                className="rounded border-[hsl(var(--border))]"
              />
              Also send an email (requires SMTP)
            </label>
            <span className="text-xs text-[hsl(var(--muted-foreground))] italic">
              An in-app notification always fires — the user sees a toast next time they open N8One.
            </span>
          </div>
        </form>
      </div>

      {/* Active grants */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] mb-2">Active complimentary grants</div>
        {!loading && grants.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No active grants.</div>}
        <ul className="space-y-2" data-testid="admin-grants-list">
          {grants.map((g) => (
            <li key={g.id} className="p-3 border hairline rounded flex items-center gap-3 flex-wrap" data-testid={`admin-grant-${g.id}`}>
              <span className="text-sm">{g.user_email || g.user_name || g.user_id}</span>
              <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-[hsl(var(--sand))]/60">{g.source}</span>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">exp: {_fmt(g.expires_at)}</span>
              {g.reason && <span className="text-xs italic text-[hsl(var(--muted-foreground))]">&ldquo;{g.reason}&rdquo;</span>}
              <button
                type="button"
                onClick={() => revokeGrant(g.id)}
                disabled={busy}
                className="ml-auto p-1 rounded hover:bg-[hsl(var(--sand))] tab-link"
                aria-label="Revoke"
                data-testid={`admin-grant-revoke-${g.id}`}
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
